import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { api } from "./api";
import SystemsDesigner from "./SystemsDesigner";
import ProfileLibrary from "./ProfileLibrary";
import BwaySystemInfo from "./BwaySystemInfo";

const ProjectPicker = ({ projects, value, onChange }) => (
  <Select value={value || ""} onValueChange={onChange}>
    <SelectTrigger data-testid="bway-project-select" className="h-9 w-full border-zinc-800 bg-zinc-900 text-sm sm:w-72"><SelectValue placeholder="Select a project" /></SelectTrigger>
    <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
  </Select>
);

export default function BwayPage() {
  const [params, setParams] = useSearchParams();
  const [projects, setProjects] = useState(null);
  const [project, setProject] = useState(null);
  const [openDesign, setOpenDesign] = useState(null);
  const pid = params.get("project");
  const did = params.get("design");

  useEffect(() => { api.get("/projects").then((r) => setProjects(r.data)); }, []);
  useEffect(() => {
    if (!projects?.length) return;
    if (!pid) { setParams({ project: projects[0].id }, { replace: true }); return; }
    api.get(`/projects/${pid}`).then((r) => setProject(r.data)).catch(() => setProject(null));
  }, [pid, projects]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (did) api.get(`/designs/${did}`).then((r) => setOpenDesign(r.data)).catch(() => setOpenDesign(null)); }, [did]);

  return (
    <div data-testid="bway-page">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-sky-400">BWAY Glazing Systems · Slim Pro 25 mm</div>
          <h1 className="mt-1 font-[Manrope] text-3xl font-extrabold tracking-tight md:text-4xl">BWAY Slimpro Curtain Wall</h1>
          <p className="mt-2 max-w-2xl text-sm text-zinc-400">A separate workspace for BWAY Slim Pro designs. Profiles come only from the BWAY / Elex section drawings and the BBM structural report. The design logic is the same as the main curtain wall tool.</p>
        </div>
        {projects?.length > 0 && <ProjectPicker projects={projects} value={pid} onChange={(v) => setParams({ project: v })} />}
      </div>
      <Tabs defaultValue="designer">
        <TabsList className="mb-6 border border-zinc-800 bg-[#121215]">
          <TabsTrigger value="designer" data-testid="bway-tab-designer">Systems Designer</TabsTrigger>
          <TabsTrigger value="profiles" data-testid="bway-tab-profiles">Profile Library</TabsTrigger>
          <TabsTrigger value="system" data-testid="bway-tab-system">System Stack</TabsTrigger>
        </TabsList>
        <TabsContent value="designer">
          {projects === null ? <Loader2 className="h-6 w-6 animate-spin text-zinc-500" />
            : projects.length === 0 ? <p className="text-sm text-zinc-400" data-testid="bway-no-projects">Create a project first on the <Link to="/dashboard" className="text-sky-400">Projects</Link> page.</p>
              : project ? <SystemsDesigner key={project.id} project={project} openDesign={openDesign} brand="bway" /> : <Loader2 className="h-6 w-6 animate-spin text-zinc-500" />}
        </TabsContent>
        <TabsContent value="profiles"><ProfileLibrary brand="bway" embedded /></TabsContent>
        <TabsContent value="system"><BwaySystemInfo /></TabsContent>
      </Tabs>
    </div>
  );
}
